import { Controller } from "@hotwired/stimulus";

const UNIT_DAYS = { days: 1, weeks: 7 };
const DAY = 86_400_000;
const LABEL = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

// This controller mirrors Protocols::BreakEditor#upcoming_break and
// #break_under_way? so the line under the date follows the fields as they change.
// The server renders the same line on load and decides what is saved.
export default class extends Controller {
  static targets = ["stretchLength", "stretchUnit", "breakLength", "breakUnit", "nextBreak", "hint"];
  static values = { today: String, upcoming: String, current: String, blank: String };

  initialize() {
    this.update = this.update.bind(this);
  }

  connect() {
    this.startDate = this.element.closest("form")?.elements.namedItem("protocol[start_date]");
    this.startDate?.addEventListener("input", this.update);
  }

  disconnect() {
    this.startDate?.removeEventListener("input", this.update);
  }

  update() {
    const stretch = this.days(this.stretchLengthTarget, this.stretchUnitTarget);
    const rest = this.days(this.breakLengthTarget, this.breakUnitTarget);
    if (!stretch || !rest) {
      this.hintTarget.textContent = this.blankValue;
      return;
    }

    const today = parse(this.todayValue);
    const chosen = parse(this.nextBreakTarget.value);
    const anchor = chosen === null ? Math.max(parse(this.startDate?.value) ?? today, today) : chosen - stretch * DAY;
    const startsOn = currentOrNextBreak(anchor, stretch, rest, today);
    const template = startsOn <= today ? this.currentValue : this.upcomingValue;
    this.hintTarget.textContent = template
      .replace("%{starts}", LABEL.format(startsOn))
      .replace("%{ends}", LABEL.format(startsOn + (rest - 1) * DAY));
  }

  days(lengthField, unitField) {
    const count = lengthField.value.trim();
    const total = /^\d+$/.test(count) ? Number(count) * (UNIT_DAYS[unitField.value] ?? 0) : 0;
    return total >= 1 && total <= 365 ? total : null;
  }
}

function parse(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value?.trim() ?? "");
  if (!match) return null;

  const time = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(time).getUTCDate() === Number(match[3]) ? time : null;
}

function currentOrNextBreak(anchor, stretch, rest, today) {
  const first = anchor + stretch * DAY;
  if (today < first) return first;

  const period = (stretch + rest) * DAY;
  const latest = first + Math.floor((today - first) / period) * period;
  return today < latest + rest * DAY ? latest : latest + period;
}
